import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { CustomDatePicker } from '../components/ui/custom-date-picker';
import { toast } from 'sonner';
import { Upload, Receipt, ArrowLeft, Users, FileText, X } from 'lucide-react';
import ErrorDialog from '../components/ErrorDialog';
import { getTodayString } from '../utils/dateUtils';
import { 
  CATEGORIES, 
  EMPLOYEE_SUB_CATEGORIES, 
  PAYMENT_METHODS, 
  STATUSES, 
  preventWheelChange,
  getCategoryConfig 
} from '../components/expenses';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

// Supported file types for R2 upload
const SUPPORTED_FILE_TYPES = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.tiff', '.tif', '.pdf', '.txt', '.docx', '.csv', '.xlsx', '.xls'];

export default function ExpenseForm() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isApprover = user.role === 'approver' || user.role === 'super_approver';
  const isSuperApprover = user.role === 'super_approver';
  // Approvers (not super) can only add expenses within the last 3 IST days
  // (today + previous 2 days). Server enforces the same window — this is UX.
  const approverDateFloor = (() => {
    if (isSuperApprover) return null;
    if (user.role !== 'approver') return null; // submitters unaffected by this rule
    const d = new Date();
    d.setDate(d.getDate() - 2);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  })();
  // Auto-approve expenses for approver/admin/super_approver
  const autoApproveStatus = isApprover ? 'approved' : 'pending';

  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [rationEmployees, setRationEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]); // Multiple files support for Other Bills/Receipt
  const [gstBillFiles, setGstBillFiles] = useState([]); // GST Bill files (multiple) - shown when GST Filing = Yes
  const [errorDialog, setErrorDialog] = useState({ open: false, message: '', fieldName: '', fieldId: '' });

  const [formData, setFormData] = useState({
    amount: '',
    date: getTodayString(),
    submitted_by: user.name || '',
    submitted_by_id: user.user_id || '',
    category: 'Material',
    sub_category: '',
    vendor_name: '',
    payment_method: 'UPI',
    status: autoApproveStatus,
    notes: '',
    gst_filing: '',
    // Employee expense fields
    employee_id: '',
    employee_name: '',
    selected_employees: [],
    // Transport fields
    charge_per_ton: '',
    total_tons: '',
    total_distance: '',
    // Auditor field
    reason: '',
    // Infra Development fields
    infra_type: '',
    infra_reason: '',
    // Silica Purchase fields
    offline_amount: '',
    online_amount: '',
    number_of_tons: '',
    amount_per_ton: '',
    offline_amount_per_ton: '',
    delivered_tons: '',
    pending_tons: '',
    gst_percentage: '',
    amount_per_ton_with_gst: ''
  });

  const categoryConfig = getCategoryConfig(formData.category, formData.sub_category);

  useEffect(() => {
    fetchUsers();
    fetchEmployees();
    fetchRationEmployees();
  }, []);

  // Set default vendor for Electricity and Silica Purchase categories
  // Also set GST Filing to Yes and disable it for Silica Purchase and Transport From Mine to Plant
  useEffect(() => {
    if (formData.category === 'Electricity') {
      setFormData(prev => ({ ...prev, vendor_name: 'Andhra Pradesh State Electricity Board' }));
    } else if (formData.category === 'Silica Purchase') {
      setFormData(prev => ({ 
        ...prev, 
        vendor_name: 'Silica Syndicate',
        payment_method: 'Cash + Bank Transfer',
        gst_filing: 'Yes'  // Default to Yes and will be disabled
      }));
    } else if (formData.category === 'Transport From Mine to Plant') {
      setFormData(prev => ({ 
        ...prev, 
        gst_filing: 'Yes'  // Default to Yes and will be disabled
      }));
    }
  }, [formData.category]);

  // Auto-calculate offline and online amounts for Silica Purchase
  useEffect(() => {
    if (formData.category === 'Silica Purchase') {
      const tons = parseFloat(formData.number_of_tons) || 0;
      const amountPerTon = parseFloat(formData.amount_per_ton) || 0;
      const offlinePerTon = parseFloat(formData.offline_amount_per_ton) || 0;
      const gstPercent = parseFloat(formData.gst_percentage) || 0;
      
      // offline_amount = tons × offline_amount_per_ton (NO GST)
      const offlineAmount = tons * offlinePerTon;
      // online_amount = tons × (amount_per_ton - offline_amount_per_ton) (before GST)
      const onlineAmountBase = tons * (amountPerTon - offlinePerTon);
      // GST only on online amount
      const onlineAmountWithGst = onlineAmountBase * (1 + gstPercent / 100);
      
      // Total = offline (no GST) + online (with GST)
      const totalAmount = offlineAmount + onlineAmountWithGst;
      
      // Calculate amount per ton with GST: Total Amount / Number of Tons
      const amountPerTonWithGst = tons > 0 ? totalAmount / tons : 0;
      
      setFormData(prev => ({ 
        ...prev, 
        offline_amount: offlineAmount > 0 ? offlineAmount.toString() : '',
        online_amount: onlineAmountBase > 0 ? onlineAmountBase.toString() : '',
        amount: totalAmount > 0 ? totalAmount.toFixed(2) : '',
        amount_per_ton_with_gst: amountPerTonWithGst > 0 ? amountPerTonWithGst.toFixed(2) : ''
      }));
    }
  }, [formData.number_of_tons, formData.amount_per_ton, formData.offline_amount_per_ton, formData.gst_percentage, formData.category]);

  // Auto-calculate delivered/pending tons for Silica Purchase
  const handleDeliveredTonsChange = (value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      const totalTons = parseFloat(formData.number_of_tons) || 0;
      const delivered = parseFloat(value) || 0;
      
      // Validate: delivered tons cannot exceed number of tons
      if (delivered > totalTons && totalTons > 0) {
        toast.error(`Delivered tons cannot exceed ${totalTons} tons`);
        return;
      }
      
      const pending = Math.max(0, totalTons - delivered);
      
      setFormData(prev => ({ 
        ...prev, 
        delivered_tons: value,
        pending_tons: value !== '' && totalTons > 0 ? pending.toFixed(2) : prev.pending_tons
      }));
    }
  };

  const handlePendingTonsChange = (value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      const totalTons = parseFloat(formData.number_of_tons) || 0;
      const pending = parseFloat(value) || 0;
      
      // Validate: pending tons cannot exceed number of tons
      if (pending > totalTons && totalTons > 0) {
        toast.error(`Pending tons cannot exceed ${totalTons} tons`);
        return;
      }
      
      const delivered = Math.max(0, totalTons - pending);
      
      setFormData(prev => ({ 
        ...prev, 
        pending_tons: value,
        delivered_tons: value !== '' && totalTons > 0 ? delivered.toFixed(2) : prev.delivered_tons
      }));
    }
  };

  // Reset sub_category when category changes
  useEffect(() => {
    if (formData.category !== 'Employee Expenses') {
      setFormData(prev => ({ ...prev, sub_category: '', employee_id: '', employee_name: '', selected_employees: [] }));
    }
  }, [formData.category]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use active employees endpoint for expense dropdown (excludes those who left 30+ days ago)
      const response = await axios.get(`${API}/employees/active/for-expenses`, { headers: { Authorization: `Bearer ${token}` } });
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      // Fallback to all employees if the new endpoint fails
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/employees`, { headers: { Authorization: `Bearer ${token}` } });
        setEmployees(response.data);
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    }
  };

  const fetchRationEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use active employees for ration (excludes anyone whose end_date has passed)
      const response = await axios.get(`${API}/employees/active/for-ration`, { headers: { Authorization: `Bearer ${token}` } });
      setRationEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch ration employees:', error);
      // Fallback to all employees
      setRationEmployees(employees);
    }
  };

  const handleEmployeeSelect = (employeeId) => {
    const employee = employees.find(e => e.employee_id === employeeId);
    if (employee) {
      setFormData(prev => ({
        ...prev,
        employee_id: employeeId,
        employee_name: employee.name,
        vendor_name: employee.name
      }));
    }
  };

  const handleMultiEmployeeToggle = (employeeId) => {
    setFormData(prev => {
      const selected = prev.selected_employees.includes(employeeId)
        ? prev.selected_employees.filter(id => id !== employeeId)
        : [...prev.selected_employees, employeeId];
      return { ...prev, selected_employees: selected };
    });
  };

  const handleSelectAllEmployees = () => {
    // For ration, use rationEmployees (excludes terminated employees)
    const employeeList = formData.sub_category === 'Ration' ? rationEmployees : employees;
    if (formData.selected_employees.length === employeeList.length) {
      setFormData(prev => ({ ...prev, selected_employees: [] }));
    } else {
      setFormData(prev => ({ ...prev, selected_employees: employeeList.map(e => e.employee_id) }));
    }
  };

  const isBillRequired = () => {
    // For Employee Expenses category, bill is not mandatory
    if (formData.category === 'Employee Expenses') return false;
    // For Local Support, attachment is optional
    if (formData.category === 'Local Support') return false;
    // For Transport From Mine to Plant and Silica Purchase, regular bill is NOT mandatory (GST Bill is mandatory instead)
    if (formData.category === 'Transport From Mine to Plant' || formData.category === 'Silica Purchase') return false;
    // When GST Filing is Yes, Other Bills/Receipt is NOT mandatory (GST Bill is mandatory instead)
    if (formData.gst_filing === 'Yes') return false;
    // For cash payment, bill is mandatory
    if (formData.payment_method === 'Cash') return true;
    // Default based on category config
    return categoryConfig.billRequired;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (isBillRequired() && files.length === 0) {
      setErrorDialog({
        open: true,
        message: 'Please upload a bill/receipt for this expense type',
        fieldName: 'Bill/Receipt',
        fieldId: 'bill-upload'
      });
      return;
    }

    if (formData.category === 'Employee Expenses') {
      if (!formData.sub_category) {
        setErrorDialog({
          open: true,
          message: 'Please select a sub-category for employee expenses',
          fieldName: 'Sub-Category',
          fieldId: 'sub_category'
        });
        return;
      }
      if (formData.sub_category === 'Ration' && formData.selected_employees.length === 0) {
        setErrorDialog({
          open: true,
          message: 'Please select at least one employee for ration expense',
          fieldName: 'Employees',
          fieldId: 'employee-checkboxes'
        });
        return;
      }
      if (formData.sub_category !== 'Ration' && !formData.employee_id) {
        setErrorDialog({
          open: true,
          message: 'Please select an employee for this expense',
          fieldName: 'Employee',
          fieldId: 'employee_id'
        });
        return;
      }
    }

    // GST Filing validation (required for all categories)
    if (!formData.gst_filing) {
      setErrorDialog({
        open: true,
        message: 'Please select GST Filing status (Yes or No)',
        fieldName: 'GST Filing',
        fieldId: 'gst_filing'
      });
      return;
    }

    // If GST Filing is Yes, GST bill upload is mandatory
    if (formData.gst_filing === 'Yes' && gstBillFiles.length === 0) {
      setErrorDialog({
        open: true,
        message: 'GST bill upload is mandatory when GST Filing is "Yes". Please upload at least one GST bill.',
        fieldName: 'GST Bill Upload',
        fieldId: 'gst_bill_files'
      });
      return;
    }

    // Silica Purchase validations
    if (formData.category === 'Silica Purchase') {
      if (formData.gst_percentage === '' || formData.gst_percentage === null || formData.gst_percentage === undefined) {
        setErrorDialog({
          open: true,
          message: 'Please enter GST percentage (enter 0 if no GST)',
          fieldName: 'GST %',
          fieldId: 'gst_percentage'
        });
        return;
      }
      if (!formData.offline_amount_per_ton || parseFloat(formData.offline_amount_per_ton) <= 0) {
        setErrorDialog({
          open: true,
          message: 'Please enter offline amount per ton',
          fieldName: 'Offline Amount Per Ton',
          fieldId: 'offline_amount_per_ton'
        });
        return;
      }
    }

    // GST Bill file validation for specific categories (Transport From Mine to Plant and Silica Purchase)
    // These categories require GST bill regardless of gst_filing selection
    if (categoryConfig.showGstBillField && gstBillFiles.length === 0) {
      setErrorDialog({
        open: true,
        message: 'Please upload the GST Bill (mandatory for this category)',
        fieldName: 'GST Bill',
        fieldId: 'gst_bill_files'
      });
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      // Basic fields
      formDataToSend.append('amount', formData.amount);
      formDataToSend.append('date', formData.date);
      formDataToSend.append('submitted_by', formData.submitted_by);
      formDataToSend.append('submitted_by_id', formData.submitted_by_id);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('payment_method', formData.payment_method);
      formDataToSend.append('status', formData.status);
      formDataToSend.append('notes', formData.notes || '');
      formDataToSend.append('gst_filing', formData.gst_filing);

      // Category-specific fields
      if (formData.category === 'Employee Expenses') {
        formDataToSend.append('sub_category', formData.sub_category);
        if (formData.sub_category === 'Ration') {
          formDataToSend.append('selected_employees', JSON.stringify(formData.selected_employees));
          formDataToSend.append('vendor_name', 'Multiple Employees');
        } else {
          formDataToSend.append('employee_id', formData.employee_id);
          formDataToSend.append('vendor_name', formData.employee_name);
        }
      } else if (formData.category === 'Infra Development') {
        // For Infra Development, store infra_type as vendor_name for display consistency
        formDataToSend.append('vendor_name', formData.infra_type);
        formDataToSend.append('infra_type', formData.infra_type);
        formDataToSend.append('infra_reason', formData.infra_reason);
      } else {
        formDataToSend.append('vendor_name', formData.vendor_name);
      }

      // Transport fields
      if (categoryConfig.showTransportFields) {
        formDataToSend.append('charge_per_ton', formData.charge_per_ton || '');
        formDataToSend.append('total_tons', formData.total_tons || '');
        formDataToSend.append('total_distance', formData.total_distance || '');
      }

      // GST Bill files (for all categories when GST Filing = Yes, or specific categories)
      if (gstBillFiles.length > 0) {
        gstBillFiles.forEach((file, index) => {
          formDataToSend.append('gst_bill_files', file);
        });
      }

      // Non-GST Bill files - send in same request (not separate attachment endpoint)
      if (files.length > 0) {
        files.forEach((file, index) => {
          formDataToSend.append('file', file);  // Use 'file' field for non-GST bills
        });
      }

      // Auditor reason
      if (formData.category === 'Auditor Expenses') {
        formDataToSend.append('reason', formData.reason);
      }

      // Silica Purchase fields
      if (formData.category === 'Silica Purchase') {
        formDataToSend.append('offline_amount', formData.offline_amount || '');
        formDataToSend.append('online_amount', formData.online_amount || '');
        formDataToSend.append('number_of_tons', formData.number_of_tons || '');
        formDataToSend.append('amount_per_ton', formData.amount_per_ton || '');
        formDataToSend.append('offline_amount_per_ton', formData.offline_amount_per_ton || '');
        formDataToSend.append('delivered_tons', formData.delivered_tons || '');
        formDataToSend.append('pending_tons', formData.pending_tons || '');
        formDataToSend.append('gst_percentage', formData.gst_percentage || '');
        formDataToSend.append('amount_per_ton_with_gst', formData.amount_per_ton_with_gst || '');
      }

      // Don't include file in initial expense creation - will upload to R2 separately
      // if (file) {
      //   formDataToSend.append('file', file);
      // }

      const response = await axios.post(`${API}/expenses`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const expenseId = response.data.id;
      toast.success('Expense added successfully!');
      navigate('/expenses');
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to add expense';
      
      if (Array.isArray(detail)) {
        // Handle FastAPI validation errors (array of objects)
        errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || JSON.stringify(detail);
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      
      // Validate file types
      const invalidFiles = selectedFiles.filter(f => {
        const ext = '.' + f.name.split('.').pop().toLowerCase();
        return !SUPPORTED_FILE_TYPES.includes(ext);
      });

      if (invalidFiles.length > 0) {
        toast.error(`Unsupported file type(s): ${invalidFiles.map(f => f.name).join(', ')}`);
        return;
      }

      // Check file sizes (10MB max each)
      const oversizedFiles = selectedFiles.filter(f => f.size > 10 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        toast.error(`File(s) exceed 10MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
        return;
      }

      setFiles(prev => [...prev, ...selectedFiles]);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid="expense-form-page">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/expenses')}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to Expenses</span>
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-slate-500 to-slate-700 rounded-2xl flex items-center justify-center shadow-lg shadow-slate-500/30">
              <Receipt className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="text-slate-500 text-sm font-bold tracking-[0.15em] uppercase">New Entry</p>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Add Expense
              </h1>
            </div>
          </div>
        </div>

        <Card className="p-8 border-0 rounded-2xl shadow-lg bg-white overflow-hidden">
          <form onSubmit={handleSubmit} className="space-y-6 overflow-x-hidden" data-testid="expense-form">
            {/* Category and Payment Method — FIRST */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="category" className="text-sm font-semibold">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value, vendor_name: '' })}
                >
                  <SelectTrigger data-testid="category-select" className="h-11 rounded-xl">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment_method" className="text-sm font-semibold">Payment Method *</Label>
                {formData.category === 'Silica Purchase' ? (
                  <Input
                    id="payment_method"
                    type="text"
                    value="Cash + Bank Transfer"
                    disabled
                    className="h-11 rounded-xl bg-slate-100 cursor-not-allowed"
                  />
                ) : (
                  <Select
                    value={formData.payment_method}
                    onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                  >
                    <SelectTrigger data-testid="payment-method-select" className="h-11 rounded-xl">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map((method) => (
                        <SelectItem key={method} value={method}>{method}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* GST Filing */}
              <div className="space-y-2">
                <Label htmlFor="gst_filing" className="text-sm font-semibold">GST Filing *</Label>
                <Select
                  value={formData.gst_filing}
                  onValueChange={(value) => setFormData({ ...formData, gst_filing: value })}
                  disabled={formData.category === 'Silica Purchase' || formData.category === 'Transport From Mine to Plant'}
                >
                  <SelectTrigger 
                    data-testid="gst-filing-select" 
                    className={`h-11 rounded-xl ${(formData.category === 'Silica Purchase' || formData.category === 'Transport From Mine to Plant') ? 'bg-slate-100 cursor-not-allowed opacity-70' : ''}`}
                  >
                    <SelectValue placeholder="Select GST Filing status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
                {(formData.category === 'Silica Purchase' || formData.category === 'Transport From Mine to Plant') && (
                  <p className="text-xs text-slate-500">GST Filing is mandatory for this category</p>
                )}
              </div>
            </div>

            {/* Amount and Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2 min-w-0">
                <Label htmlFor="amount" className="text-sm font-semibold">
                  Amount (₹) *
                  {formData.category === 'Silica Purchase' && (
                    <span className="text-xs text-slate-500 ml-2">(Auto-calculated from Offline + Online)</span>
                  )}
                </Label>
                <Input
                  id="amount"
                  data-testid="amount-input"
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*\.?[0-9]*"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => {
                    if (formData.category !== 'Silica Purchase') {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setFormData({ ...formData, amount: value });
                      }
                    }
                  }}
                  required
                  disabled={formData.category === 'Silica Purchase'}
                  className={`h-11 rounded-xl ${formData.category === 'Silica Purchase' ? 'bg-slate-100 cursor-not-allowed' : ''}`}
                />
              </div>

              <div className="space-y-2 min-w-0">
                <Label htmlFor="date" className="text-sm font-semibold">Date *</Label>
                <CustomDatePicker
                  value={formData.date}
                  onChange={(date) => {
                    const today = getTodayString();
                    if (date && date > today) {
                      toast.error('Date cannot be in the future');
                      return;
                    }
                    if (approverDateFloor && date && date < approverDateFloor) {
                      toast.error('Approvers can only add expenses for today or the last 2 days. Contact a Super Approver for older dates.');
                      return;
                    }
                    setFormData({ ...formData, date: date });
                  }}
                  min={approverDateFloor || undefined}
                  placeholder="Select date"
                  className="w-full"
                />
                {approverDateFloor && (
                  <p className="text-xs text-slate-500 mt-1" data-testid="approver-date-window-hint">
                    You can select today or the previous 2 days.
                  </p>
                )}
              </div>
            </div>

            {/* Submitted By - Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="submitted_by" className="text-sm font-semibold">Submitted By *</Label>
              <Select
                value={formData.submitted_by_id}
                onValueChange={(value) => {
                  const selectedUser = users.find(u => u.user_id === value);
                  setFormData({ 
                    ...formData, 
                    submitted_by_id: value,
                    submitted_by: selectedUser?.name || ''
                  });
                }}
              >
                <SelectTrigger data-testid="submitted-by-select" className="h-11 rounded-xl">
                  <SelectValue placeholder="Select user">{formData.submitted_by || 'Select user'}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sub-Category for Employee Expenses */}
            {categoryConfig.showSubCategory && (
              <div className="space-y-2">
                <Label htmlFor="sub_category" className="text-sm font-semibold">Sub-Category *</Label>
                <Select
                  value={formData.sub_category}
                  onValueChange={(value) => setFormData({ ...formData, sub_category: value, employee_id: '', employee_name: '', selected_employees: [] })}
                >
                  <SelectTrigger data-testid="sub-category-select" className="h-11 rounded-xl">
                    <SelectValue placeholder="Select sub-category" />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYEE_SUB_CATEGORIES.map((sub) => (
                      <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Employee Select (Single) */}
            {categoryConfig.showEmployeeSelect && formData.sub_category && formData.sub_category !== 'Ration' && (
              <div className="space-y-2">
                <Label htmlFor="employee" className="text-sm font-semibold">Employee Name *</Label>
                <Select
                  value={formData.employee_id}
                  onValueChange={handleEmployeeSelect}
                >
                  <SelectTrigger data-testid="employee-select" className="h-11 rounded-xl">
                    <SelectValue placeholder="Select employee">{formData.employee_name || 'Select employee'}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.employee_id} value={emp.employee_id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Multi-Employee Select for Ration */}
            {categoryConfig.showMultiEmployeeSelect && formData.sub_category === 'Ration' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Select Employees *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllEmployees}
                    className="text-xs"
                  >
                    {formData.selected_employees.length === rationEmployees.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                <div className="border rounded-xl p-4 max-h-48 overflow-y-auto space-y-2 bg-slate-50">
                  {rationEmployees.map((emp) => (
                    <div key={emp.employee_id} className="flex items-center gap-3 p-2 hover:bg-white rounded-lg transition-colors">
                      <Checkbox
                        id={emp.employee_id}
                        checked={formData.selected_employees.includes(emp.employee_id)}
                        onCheckedChange={() => handleMultiEmployeeToggle(emp.employee_id)}
                      />
                      <label htmlFor={emp.employee_id} className="text-sm cursor-pointer flex-1">
                        {emp.name}
                      </label>
                    </div>
                  ))}
                </div>
                {formData.selected_employees.length > 0 && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {formData.selected_employees.length} employee(s) selected - Amount will be split equally (₹{formData.amount ? (parseFloat(formData.amount) / formData.selected_employees.length).toFixed(2) : '0.00'} each)
                  </p>
                )}
              </div>
            )}

            {/* Vendor Name Field (Dynamic based on category) */}
            {!categoryConfig.showEmployeeSelect && !categoryConfig.showMultiEmployeeSelect && !categoryConfig.showInfraFields && (
              <div className="space-y-2">
                <Label htmlFor="vendor_name" className="text-sm font-semibold">{categoryConfig.vendorField} *</Label>
                <Input
                  id="vendor_name"
                  data-testid="vendor-name-input"
                  type="text"
                  placeholder={categoryConfig.vendorPlaceholder}
                  value={formData.vendor_name}
                  onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                  required
                  disabled={!categoryConfig.vendorEditable}
                  className={`h-11 rounded-xl ${!categoryConfig.vendorEditable ? 'bg-slate-100 cursor-not-allowed' : ''}`}
                />
              </div>
            )}

            {/* Reason field for Auditor Expenses */}
            {categoryConfig.showReason && (
              <div className="space-y-2">
                <Label htmlFor="reason" className="text-sm font-semibold">Reason *</Label>
                <Input
                  id="reason"
                  data-testid="reason-input"
                  type="text"
                  placeholder="Enter reason for expense"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  required
                  className="h-11 rounded-xl"
                />
              </div>
            )}

            {/* Infra Development Fields */}
            {categoryConfig.showInfraFields && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="infra_type" className="text-sm font-semibold">Type of Infra *</Label>
                  <Input
                    id="infra_type"
                    data-testid="infra-type-input"
                    type="text"
                    placeholder="Enter type of infrastructure"
                    value={formData.infra_type}
                    onChange={(e) => setFormData({ ...formData, infra_type: e.target.value })}
                    required
                    className="h-11 rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="infra_reason" className="text-sm font-semibold">Reason *</Label>
                  <Input
                    id="infra_reason"
                    data-testid="infra-reason-input"
                    type="text"
                    placeholder="Enter reason for this expense"
                    value={formData.infra_reason}
                    onChange={(e) => setFormData({ ...formData, infra_reason: e.target.value })}
                    required
                    className="h-11 rounded-xl"
                  />
                </div>
              </div>
            )}

            {/* Transport Fields */}
            {categoryConfig.showTransportFields && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="charge_per_ton" className="text-sm font-semibold">Charge per Ton (₹)</Label>
                  <Input
                    id="charge_per_ton"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.charge_per_ton}
                    onChange={(e) => setFormData({ ...formData, charge_per_ton: e.target.value })}
                    onWheel={preventWheelChange}
                    className="h-11 rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="total_tons" className="text-sm font-semibold">Total Tons</Label>
                  <Input
                    id="total_tons"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.total_tons}
                    onChange={(e) => setFormData({ ...formData, total_tons: e.target.value })}
                    onWheel={preventWheelChange}
                    className="h-11 rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="total_distance" className="text-sm font-semibold">Total Distance (km)</Label>
                  <Input
                    id="total_distance"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.total_distance}
                    onChange={(e) => setFormData({ ...formData, total_distance: e.target.value })}
                    onWheel={preventWheelChange}
                    className="h-11 rounded-xl"
                  />
                </div>
              </div>
            )}

            {/* Silica Purchase Fields */}
            {categoryConfig.showSilicaFields && (
              <>
                {/* Number of Tons, Amount per Ton, Offline Amount per Ton */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="number_of_tons" className="text-sm font-semibold">Number of Tons *</Label>
                    <Input
                      id="number_of_tons"
                      data-testid="number-of-tons-input"
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      placeholder="0.00"
                      value={formData.number_of_tons}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setFormData({ ...formData, number_of_tons: value });
                        }
                      }}
                      className="h-11 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="amount_per_ton" className="text-sm font-semibold">Amount per Ton (₹) *</Label>
                    <Input
                      id="amount_per_ton"
                      data-testid="amount-per-ton-input"
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      placeholder="0.00"
                      value={formData.amount_per_ton}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setFormData({ ...formData, amount_per_ton: value });
                        }
                      }}
                      className="h-11 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="offline_amount_per_ton" className="text-sm font-semibold">Offline Amount per Ton (₹) *</Label>
                    <Input
                      id="offline_amount_per_ton"
                      data-testid="offline-amount-per-ton-input"
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      placeholder="0.00"
                      value={formData.offline_amount_per_ton}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setFormData({ ...formData, offline_amount_per_ton: value });
                        }
                      }}
                      className="h-11 rounded-xl"
                    />
                  </div>
                </div>

                {/* Offline and Online Amounts (Auto-calculated, grayed out) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="offline_amount" className="text-sm font-semibold">
                      Offline Amount (₹)
                      <span className="text-xs text-slate-500 ml-2">(Auto-calculated)</span>
                    </Label>
                    <Input
                      id="offline_amount"
                      data-testid="offline-amount-input"
                      type="text"
                      placeholder="0.00"
                      value={formData.offline_amount}
                      disabled
                      className="h-11 rounded-xl bg-slate-100 cursor-not-allowed"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="online_amount" className="text-sm font-semibold">
                      Online Amount (₹)
                      <span className="text-xs text-slate-500 ml-2">(Auto-calculated)</span>
                    </Label>
                    <Input
                      id="online_amount"
                      data-testid="online-amount-input"
                      type="text"
                      placeholder="0.00"
                      value={formData.online_amount}
                      disabled
                      className="h-11 rounded-xl bg-slate-100 cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* GST Percentage */}
                <div className="space-y-2">
                  <Label htmlFor="gst_percentage" className="text-sm font-semibold">GST % *</Label>
                  <Input
                    id="gst_percentage"
                    data-testid="gst-percentage-input"
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    placeholder="0, 5, 12, 18, 28"
                    value={formData.gst_percentage}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (/^\d*\.?\d*$/.test(value) && parseFloat(value) <= 100)) {
                        setFormData({ ...formData, gst_percentage: value });
                      }
                    }}
                    className="h-11 rounded-xl"
                    required
                  />
                  <p className="text-xs text-slate-500">GST will be added to Amount automatically (enter 0 if no GST)</p>
                </div>

                {/* Amount per Ton with GST (Auto-calculated) */}
                <div className="space-y-2">
                  <Label htmlFor="amount_per_ton_with_gst" className="text-sm font-semibold">
                    Amount per Ton with GST (₹)
                    <span className="text-xs text-slate-500 ml-2">(Auto-calculated)</span>
                  </Label>
                  <Input
                    id="amount_per_ton_with_gst"
                    data-testid="amount-per-ton-with-gst-input"
                    type="text"
                    placeholder="0.00"
                    value={formData.amount_per_ton_with_gst}
                    disabled
                    className="h-11 rounded-xl bg-green-100 cursor-not-allowed font-semibold"
                  />
                  <p className="text-xs text-green-600">= Total Amount ÷ Number of Tons (used for inventory calculation)</p>
                </div>

                {/* Delivered and Pending Tons */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="delivered_tons" className="text-sm font-semibold">Delivered Tons *</Label>
                    <Input
                      id="delivered_tons"
                      data-testid="delivered-tons-input"
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      placeholder="0.00"
                      value={formData.delivered_tons}
                      onChange={(e) => handleDeliveredTonsChange(e.target.value)}
                      className="h-11 rounded-xl"
                    />
                    <p className="text-xs text-slate-500">Auto-calculates pending tons</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pending_tons" className="text-sm font-semibold">Pending Tons *</Label>
                    <Input
                      id="pending_tons"
                      data-testid="pending-tons-input"
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      placeholder="0.00"
                      value={formData.pending_tons}
                      onChange={(e) => handlePendingTonsChange(e.target.value)}
                      className="h-11 rounded-xl"
                    />
                    <p className="text-xs text-slate-500">Auto-calculates delivered tons</p>
                  </div>
                </div>
              </>
            )}

            {/* Status (Approvers only) */}
            {isApprover && (
              <div className="space-y-2">
                <Label htmlFor="status" className="text-sm font-semibold">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="status-select" className="h-11 rounded-xl">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-sm font-semibold">Description (Optional)</Label>
              <Textarea
                id="notes"
                data-testid="notes-textarea"
                placeholder="Additional details about this expense..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="rounded-xl resize-none"
              />
            </div>

            {/* GST Bill File Upload - Shows when GST Filing = Yes (all categories) or for specific categories */}
            {(formData.gst_filing === 'Yes' || categoryConfig.showGstBillField) && (
              <div className="space-y-2">
                <Label htmlFor="gst_bill_files" className="text-sm font-semibold">Upload GST Bill(s) *</Label>
                <div className={`border-2 border-dashed rounded-xl p-4 text-center transition-all cursor-pointer ${gstBillFiles.length === 0 ? 'border-red-300 hover:border-red-400 bg-red-50/50' : 'border-green-300 bg-green-50/50'}`}>
                  <input
                    id="gst_bill_files"
                    data-testid="gst-bill-files-input"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    multiple
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        const newFiles = Array.from(e.target.files);
                        const validFiles = newFiles.filter(file => {
                          if (file.size > 10 * 1024 * 1024) {
                            toast.error(`File "${file.name}" exceeds 10MB limit`);
                            return false;
                          }
                          return true;
                        });
                        setGstBillFiles(prev => [...prev, ...validFiles]);
                      }
                    }}
                    className="hidden"
                  />
                  <label htmlFor="gst_bill_files" className="cursor-pointer flex flex-col items-center gap-2">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${gstBillFiles.length > 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                      <Upload className={`w-4 h-4 ${gstBillFiles.length > 0 ? 'text-green-600' : 'text-red-500'}`} />
                    </div>
                    <span className={`text-sm font-medium ${gstBillFiles.length > 0 ? 'text-green-700' : 'text-red-600'}`}>
                      {gstBillFiles.length > 0 ? `${gstBillFiles.length} GST Bill(s) selected` : 'Click to upload GST Bill(s)'}
                    </span>
                    <span className="text-xs text-slate-500">Mandatory • Multiple files allowed • PDF, PNG, JPG (Max 10MB each)</span>
                  </label>
                </div>
                {/* GST Bill Files List */}
                {gstBillFiles.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {gstBillFiles.map((f, index) => (
                      <div key={index} className="flex items-center justify-between bg-green-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-green-600" />
                          <span className="text-sm text-slate-700 truncate max-w-[200px]">{f.name}</span>
                          <span className="text-xs text-slate-400">({(f.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setGstBillFiles(prev => prev.filter((_, i) => i !== index))}
                          className="p-1 hover:bg-red-100 rounded-full transition-colors"
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setGstBillFiles([])}
                      className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Remove All GST Bills
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Non-GST Bills/Receipts Upload */}
            <div className="space-y-2">
              <Label htmlFor="file" className="text-sm font-semibold">
                Upload Non-GST Bills/Receipts {isBillRequired() && <span className="text-red-500">*</span>}
              </Label>
              <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${isBillRequired() && files.length === 0 ? 'border-red-300 hover:border-red-400 bg-red-50/50' : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50/50'}`}>
                <input
                  id="file"
                  data-testid="file-input"
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  multiple
                  accept={SUPPORTED_FILE_TYPES.join(',')}
                />
                <label htmlFor="file" className="cursor-pointer flex flex-col items-center gap-2">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${files.length > 0 ? 'bg-green-100' : 'bg-slate-100'}`}>
                    <Upload className={`w-5 h-5 ${files.length > 0 ? 'text-green-600' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700 block">
                      {files.length > 0 ? `${files.length} file(s) selected` : 'Click to upload files'}
                    </span>
                    <span className="text-xs text-slate-400">
                      {isBillRequired() ? 'Required for this expense' : 'Optional'} • Multiple files will be merged into one PDF
                    </span>
                  </div>
                </label>
              </div>
              
              {/* Selected Files List */}
              {files.length > 0 && (
                <div className="space-y-2 mt-3">
                  {files.map((f, index) => (
                    <div key={index} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-slate-700 truncate max-w-[200px]">{f.name}</span>
                        <span className="text-xs text-slate-400">({(f.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="p-1 hover:bg-red-100 rounded-full transition-colors"
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-xs text-slate-500">
                Supported: PDF, Images (PNG, JPG, GIF), Documents (DOCX, TXT), Spreadsheets (XLSX, CSV)
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <Button
                data-testid="cancel-button"
                type="button"
                onClick={() => navigate('/expenses')}
                variant="outline"
                className="flex-1 h-12 rounded-xl font-semibold"
              >
                Cancel
              </Button>
              <Button
                data-testid="save-expense-btn"
                type="submit"
                className="flex-1 h-12 bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 text-white rounded-xl font-semibold shadow-lg shadow-slate-900/30"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Expense'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
      
      {/* Error Dialog */}
      <ErrorDialog
        open={errorDialog.open}
        onClose={() => setErrorDialog({ ...errorDialog, open: false })}
        message={errorDialog.message}
        fieldName={errorDialog.fieldName}
        fieldId={errorDialog.fieldId}
      />
    </Layout>
  );
}
